import { api } from "@/lib/http";

export type GuardrailApp =
  | "SLACK"
  | "SECURE_LLM"
  | "GOOGLE_DRIVE"
  | "EMAIL_DLP"
  | "DESKTOP_OVERLAY"
  | "GATEWAY"
  | (string & {});

export type GuardrailFeatureSettings = {
  toxicity?: {
    id: number;
    name: string;
    description: string;
    settings: {
      threshold: number;
    };
    is_active: boolean;
  };
  language?: {
    id: number;
    name: string;
    description: string;
    settings: {
      min_coverage: number;
      min_confidence: number;
      allowed_languages: Array<string>;
    };
    is_active: boolean;
  };
  topic?: {
    id: number;
    name: string;
    description: string;
    settings: {
      threshold: number;
      disallowed_topics: Array<string>;
    };
    is_active: boolean;
  };
  code?: {
    id: number;
    name: string;
    description: string;
    settings: {
      allowed_languages: Array<string>;
      allow_code_snippets: boolean;
    };
    is_active: boolean;
  };
  sentiment?: {
    id: number;
    name: string;
    description: string;
    settings: {
      negative_threshold: number;
      positive_threshold: number;
    };
    is_active: boolean;
  };
  gibberish?: {
    id: number;
    name: string;
    description: string;
    settings: Record<string, never>;
    is_active: boolean;
  };
};

export type GuardrailFeaturesResponse = {
  source: GuardrailApp;
  settings: GuardrailFeatureSettings;
};

export type UpdateGuardrailSettingsPayload = {
  settings: Record<string, unknown>;
  is_active: boolean;
};

async function getApps() {
  return api.raw.get("api/v1/guardrails/apps").json<Array<GuardrailApp>>();
}

async function getFeatures(app: GuardrailApp) {
  return api.raw
    .get(`api/v1/guardrails/features/${app}`)
    .json<GuardrailFeaturesResponse>();
}

async function updateSettings(
  id: number,
  payload: UpdateGuardrailSettingsPayload,
) {
  return api.raw
    .patch(`api/v1/guardrails/settings/${id}`, {
      json: payload,
    })
    .json();
}

// Analysis API types and functions
export type LanguageAnalysisPayload = {
  allowed_languages: Array<string>;
  min_confidence: number;
  min_coverage: number;
  text: string;
};

export type LanguageDetection = {
  sentence: string;
  language: string;
  language_code: string;
  score: number;
};

export type LanguageAnalysisResponse = {
  detections: Array<LanguageDetection>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
  primary_language: string;
  primary_language_confidence: number;
  primary_language_coverage: number;
  language_coverages: Record<string, number>;
};

export type ToxicityAnalysisPayload = {
  threshold: number;
  text: string;
};

export type ToxicityAnalysisResponse = {
  label: string;
  score: number;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type TopicsAnalysisPayload = {
  threshold: number;
  disallowed_topics: Array<string>;
  text: string;
  app_ref: string;
};

export type TopicsAnalysisResponse = {
  flagged_topics: Record<string, number>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type CodeAnalysisPayload = {
  allow_code_snippets: boolean;
  allowed_languages: Array<string>;
  text: string;
};

export type CodeSpan = {
  text: string;
  language: string;
  start: number;
  end: number;
};

export type CodeAnalysisResponse = {
  code_spans: Array<CodeSpan>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type SentimentAnalysisPayload = {
  negative_threshold: number;
  positive_threshold: number;
  text: string;
};

export type SentimentAnalysisResponse = {
  label: string;
  score: number;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type GibberishAnalysisPayload = {
  text: string;
};

export type GibberishAnalysisResponse = {
  is_gibberish: boolean;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

async function analyzeLanguage(payload: LanguageAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/language", { json: payload })
    .json<LanguageAnalysisResponse>();
}

async function analyzeToxicity(payload: ToxicityAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/toxicity", { json: payload })
    .json<ToxicityAnalysisResponse>();
}

async function analyzeTopics(payload: TopicsAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/topics", { json: payload })
    .json<TopicsAnalysisResponse>();
}

async function analyzeCode(payload: CodeAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/code", { json: payload })
    .json<CodeAnalysisResponse>();
}

async function analyzeSentiment(payload: SentimentAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/sentiment", { json: payload })
    .json<SentimentAnalysisResponse>();
}

async function analyzeGibberish(payload: GibberishAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/gibberish", { json: payload })
    .json<GibberishAnalysisResponse>();
}

export type GuardrailAction = "block" | "flag" | "redact";

export type GuardrailEnumOption = {
  value: string;
  label: string;
};

export type GuardrailSettingsUi = "multiselect" | "tags" | "toggle";

export type GuardrailSettingsProperty = {
  type: string;
  items?: { type: string };
  title?: string;
  minItems?: number;
  "x-ui"?: GuardrailSettingsUi;
  "x-enum-ref"?: string;
};

export type GuardrailCatalogItem = {
  key: string;
  feature_id: number;
  display_name: string;
  description: string;
  supported_actions: Array<GuardrailAction>;
  supports_spans: boolean;
  supports_output_direction: boolean;
  typical_latency_ms: number | null;
  default_settings: Record<string, unknown>;
  settings_schema: {
    type: "object";
    properties?: Record<string, GuardrailSettingsProperty>;
    required?: Array<string>;
  };
};

export type GuardrailCatalogResponse = {
  service_version: string | null;
  guardrails: Array<GuardrailCatalogItem>;
  enums: Record<string, Array<GuardrailEnumOption>>;
};

export type GuardrailTestItem = {
  key: string;
  action: GuardrailAction;
  settings?: Record<string, unknown>;
};

export type GuardrailTestDirection = "input" | "output";

export type GuardrailTestPayload = {
  text: string;
  direction: GuardrailTestDirection;
  guardrails: Array<GuardrailTestItem>;
};

export type GuardrailTestSpan = {
  start: number;
  end: number;
  text: string;
  label: string;
};

export type GuardrailTestResult = {
  triggered: boolean;
  severity: string | null;
  summary: string | null;
  evidence: unknown;
  spans?: Array<GuardrailTestSpan>;
  latency_ms: number;
  error: string | null;
};

export type GuardrailVerdict =
  | "blocked"
  | "redacted"
  | "allowed"
  | (string & {});

export type GuardrailTestResponse = {
  token_count: number;
  total_latency_ms: number;
  truncated: boolean;
  verdict: GuardrailVerdict;
  blocked_by: Array<string>;
  redacted_by: Array<string>;
  flagged_by: Array<string>;
  redacted_preview: string | null;
  results: Partial<Record<string, GuardrailTestResult>>;
};

async function getCatalog() {
  return api.raw
    .get("api/v1/guardrails/catalog")
    .json<GuardrailCatalogResponse>();
}

async function testGuardrails(payload: GuardrailTestPayload) {
  return api.raw
    .post("api/v1/guardrails/test", { json: payload })
    .json<GuardrailTestResponse>();
}

const guardrails = {
  getApps,
  getFeatures,
  updateSettings,
  analyzeLanguage,
  analyzeToxicity,
  analyzeTopics,
  analyzeCode,
  analyzeSentiment,
  analyzeGibberish,
  getCatalog,
  testGuardrails,
};

export default guardrails;
